import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { ReportTemplate } from 'types';
import { addParamsToUrl } from 'utils/query';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  ListRequest,
  ListResponse,
  UpdateRequest,
  CreateRequest,
  DetailsRequest,
  DetailsResponse,
} from './types';

const tagType = 'REPORT_TEMPLATES' as const;

export const reportTemplatesApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'reportTemplatesApi',
  endpoints: (build) => ({
    delete: build.mutation<void, { id: string }>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id }) => ({
        method: 'delete',
        url: URL.REPORT_TEMPLATE_DETAILS.replace(':id', id),
      }),
    }),
    update: build.mutation<DetailsResponse, UpdateRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.REPORT_TEMPLATE_DETAILS.replace(':id', id),
      }),
    }),

    create: build.mutation<ReportTemplate, CreateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: any) => response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.REPORT_TEMPLATES,
      }),
    }),

    list: build.query<ReportTemplate[], ListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: ListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.REPORT_TEMPLATES, params),
      }),
    }),

    details: build.query<ReportTemplate, DetailsRequest>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: DetailsResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.REPORT_TEMPLATE_DETAILS.replace(':id', id),
      }),
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useUpdateMutation,
  useCreateMutation,
  useDeleteMutation,
} = reportTemplatesApi;

export type { CreateRequest, UpdateRequest };
